import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spinner, Modal, Row, Col } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { FiDownload, FiEye, FiCalendar, FiUsers, FiDollarSign, FiRefreshCw, FiX, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import axios from 'axios';

const RecentReports = () => {
    const [recentReports, setRecentReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    const fetchRecentReports = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://www.azzenterprises.com/api/reports/recent-report');
            setRecentReports(response.data.reports || []);
        } catch (error) {
            console.error('Error fetching recent reports:', error);
            toast.error('Failed to load recent reports');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecentReports();
    }, [fetchRecentReports]);

    const formatCurrency = (amount) => {
        if (!amount) return "S$0.00";
        return new Intl.NumberFormat("en-SG", {
            style: "currency",
            currency: "SGD",
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('DD MMM YYYY, hh:mm A');
    };

    const getRelativeTime = (dateString) => {
        return moment(dateString).fromNow();
    };

    const handleDownload = async (report) => {
        try {
            const fileUrl = report.reports_s3;
            const fileName = fileUrl.split('/').pop() || `emi-report-${moment().format('YYYY-MM-DD-HHmm')}.txt`;

            // Create a temporary link to download the file
            const link = document.createElement('a');
            link.href = `${fileUrl}?t=${Date.now()}`;
            link.setAttribute('download', fileName);
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Download initiated');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download report');
        }
    };

    const handleViewDetails = (report) => {
        setSelectedReport(report);
        setShowDetails(true);
    };

    const getCustomerCount = (customers) => {
        if (!customers) return 0;
        try {
            const parsed = typeof customers === 'string' ? JSON.parse(customers) : customers;
            return Array.isArray(parsed) ? parsed.length : 0;
        } catch {
            return 0;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Paid": return "#16a34a";
            case "Unpaid": return "#dc2626";
            case "Pending": return "#d97706";
            default: return "#6b7280";
        }
    };

    const calculateEMIDetails = (customer) => {
        const billAmount = parseFloat(customer.bill_amount) || 0;
        const emiPlan = parseInt(customer.emi_plan_duration) || 1;
        const monthlyEMI = billAmount / emiPlan;

        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = customer.reports?.find(report => report.month === currentMonth);
        const status = currentMonthReport?.status || "Not Started";

        return { monthlyEMI, status };
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "60px",
            sortable: true
        },
        {
            name: 'DATE GENERATED',
            selector: row => row.created_date,
            sortable: true,
            cell: (row) => (
                <div>
                    <div className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                        {formatDate(row.created_date)}
                    </div>
                    <div className="text-muted small">
                        {getRelativeTime(row.created_date)}
                    </div>
                </div>
            ),
            width: '200px'
        },
        {
            name: 'CUSTOMERS',
            selector: row => getCustomerCount(row.customers),
            sortable: true,
            cell: (row) => {
                const customerCount = getCustomerCount(row.customers);
                return (
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-primary bg-opacity-10 p-2 rounded">
                            <FiUsers className="text-primary" size={16} />
                        </div>
                        <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                                {customerCount}
                            </div>
                            <div className="text-muted small">customers</div>
                        </div>
                    </div>
                );
            },
            width: '150px'
        },
        {
            name: 'TOTAL AMOUNT',
            selector: row => row.deduction_amount,
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <div className="bg-success bg-opacity-10 p-2 rounded">
                        <FiDollarSign className="text-success" size={16} />
                    </div>
                    <div>
                        <div className="fw-bold text-success" style={{ fontSize: '14px' }}>
                            {formatCurrency(row.deduction_amount)}
                        </div>
                        <div className="text-muted small">total deduction</div>
                    </div>
                </div>
            ),
            width: '180px'
        },
        {
            name: 'ACTIONS',
            cell: (row) => (
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleViewDetails(row)}
                        style={{ fontSize: '12px' }}
                    >
                        <FiEye size={14} />
                        View
                    </Button>
                    <Button
                        variant="outline-success"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleDownload(row)}
                        style={{ fontSize: '12px' }}
                    >
                        <FiDownload size={14} />
                        Download
                    </Button>
                </div>
            ),
            minWidth: '200px',
            right: true
        }
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: "#f8f9fa",
                fontWeight: "600",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#374151",
                borderBottom: "2px solid #e5e7eb",
                padding: "12px 8px"
            },
        },
        cells: {
            style: {
                fontSize: "14px",
                padding: "16px 8px",
                color: "#374151"
            }
        },
        rows: {
            style: {
                fontSize: "14px",
                '&:not(:last-of-type)': {
                    borderBottom: "1px solid #e5e7eb"
                }
            }
        }
    };

    if (loading) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-3 text-muted">Loading recent reports...</div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="fw-bold mb-1" style={{ fontSize: '16px' }}>Recent Reports</h6>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                    Recently generated EMI payment reports
                                </p>
                            </div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={fetchRecentReports}
                                className="d-flex align-items-center gap-2"
                            >
                                <FiRefreshCw size={14} />
                                Refresh
                            </Button>
                        </div>
                    </div>

                    <div className="p-3">
                        {recentReports.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="bg-light rounded-circle d-inline-flex p-3 mb-3">
                                    <FiFileText size={32} className="text-muted" />
                                </div>
                                <h6 className="text-muted mb-2">No reports generated yet</h6>
                                <p className="text-muted small">
                                    Generate your first EMI payment report to see it here
                                </p>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={recentReports}
                                pagination
                                paginationPerPage={5}
                                paginationRowsPerPageOptions={[5, 10, 15]}
                                paginationComponentOptions={{
                                    rowsPerPageText: 'Rows per page:',
                                    rangeSeparatorText: 'of',
                                }}
                                highlightOnHover
                                responsive
                                striped
                                customStyles={customStyles}
                                noDataComponent={
                                    <div className="text-center py-4 text-muted">
                                        No reports found
                                    </div>
                                }
                            />
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Details Modal */}
            <Modal show={showDetails} onHide={() => setShowDetails(false)} size="xl" centered>
                <Modal.Header className="bg-light border-bottom-0">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <Modal.Title className="fw-bold text-dark">
                                Report Details
                            </Modal.Title>
                            <p className="text-muted mb-0 small">
                                Generated on {selectedReport && formatDate(selectedReport.created_date)}
                            </p>
                        </div>
                        <Button
                            variant="link"
                            className="text-muted p-0"
                            onClick={() => setShowDetails(false)}
                        >
                            <FiX size={20} />
                        </Button>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Card className="border-0 bg-primary bg-opacity-10">
                                <Card.Body className="text-center">
                                    <FiUsers size={24} className="text-primary mb-2" />
                                    <h4 className="fw-bold text-primary">
                                        {selectedReport && getCustomerCount(selectedReport.customers)}
                                    </h4>
                                    <small className="text-muted">Total Customers</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 bg-success bg-opacity-10">
                                <Card.Body className="text-center">
                                    <FiDollarSign size={24} className="text-success mb-2" />
                                    <h4 className="fw-bold text-success">
                                        {selectedReport && formatCurrency(selectedReport.deduction_amount)}
                                    </h4>
                                    <small className="text-muted">Total Amount</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 bg-warning bg-opacity-10">
                                <Card.Body className="text-center">
                                    <FiCalendar size={24} className="text-warning mb-2" />
                                    <h6 className="fw-bold text-warning">
                                        {selectedReport && formatDate(selectedReport.created_date)}
                                    </h6>
                                    <small className="text-muted">Generated Date</small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Customer List */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h6 className="fw-bold mb-0">Customer Details</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th style={{ fontSize: '14px', fontWeight: '600' }}>Customer</th>
                                            <th style={{ fontSize: '14px', fontWeight: '600' }}>Product</th>
                                            <th style={{ fontSize: '14px', fontWeight: '600' }}>Monthly EMI</th>
                                            <th style={{ fontSize: '14px', fontWeight: '600' }}>Status</th>
                                            <th style={{ fontSize: '14px', fontWeight: '600' }}>Due Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedReport && selectedReport.customers &&
                                            getCustomerCount(selectedReport.customers) > 0 ? (
                                            selectedReport.customers.map((customer, index) => {
                                                const { monthlyEMI, status } = calculateEMIDetails(customer);
                                                return (
                                                    <tr key={index}>
                                                        <td style={{ fontSize: '14px' }}>
                                                            <div className="fw-semibold">{customer.full_name}</div>
                                                        </td>
                                                        <td style={{ fontSize: '14px' }}>
                                                            {customer.product_id || "—"}
                                                        </td>
                                                        <td style={{ fontSize: '14px' }} className="fw-semibold text-success">
                                                            {formatCurrency(monthlyEMI)}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className="badge"
                                                                style={{
                                                                    backgroundColor: getStatusColor(status),
                                                                    color: 'white',
                                                                    fontSize: '12px',
                                                                    padding: '4px 8px'
                                                                }}
                                                            >
                                                                {status}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: '14px' }}>
                                                            {customer.payment_date
                                                                ? moment(customer.payment_date).format("DD MMM")
                                                                : "—"
                                                            }
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted">
                                                    No customer data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </Card>
                </Modal.Body>

                <Modal.Footer className="bg-light border-0" style={{ fontSize: '14px' }}>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowDetails(false)}
                    >
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => selectedReport && handleDownload(selectedReport)}
                        className="d-flex align-items-center gap-2"
                    >
                        <FiDownload size={14} />
                        Download Report
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RecentReports;